import type { MouseEvent as ReactMouseEvent, SyntheticEvent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePopover } from '../Popover/usePopover';
import type { OptionsDropdownMenuOption, UseOptionsDropdownMenuParams, UseOptionsDropdownMenuReturn } from './types';

/**
 * Ids de opción que, por contrato del v1, mantienen el menú abierto al pulsarse
 * (submenús / date-pickers que necesitan seguir visibles). Para casos nuevos
 * usar la flag `shouldKeepOpenOnClick` de la opción en lugar de ampliar esta lista.
 */
const KEEP_OPEN_OPTION_IDS = new Set(['moveTo', 'dueDate', 'assignTo', 'publishScoreDate']);

function shouldKeepMenuOpen(option: OptionsDropdownMenuOption): boolean {
  return option.shouldKeepOpenOnClick ?? KEEP_OPEN_OPTION_IDS.has(option.id ?? '');
}

// Solo detenemos la propagación al card padre. NO usamos preventDefault aquí:
// cancelaría la acción nativa del navegador y rompería los controles de
// formulario de los children (p. ej. los checkboxes de StudentsPickyList, que
// se basan en onChange).
function stopPropagationOnChildrenClick(event: SyntheticEvent): void {
  event.stopPropagation();
}

/**
 * Lógica del `OptionsDropdownMenu`. Concentra la traducción, el estado del
 * `Popover` (`usePopover`) y los handlers, dejando el componente como JSX puro.
 *
 * Dos cierres distintos (patrón WAI-ARIA APG, ver `usePopover`):
 *  - `onClickInOption` cierra con `close()` (devuelve el foco al trigger) al
 *    activar una opción, salvo que la opción mantenga el menú abierto.
 *  - `onDismiss` cierra con `dismiss()` (sin mover el foco) en click-away/Escape.
 * Ambos propagan a `closeRelatedDropDowns` para cerrar dropdowns hermanos.
 */
function useOptionsDropdownMenu({ ariaLabel, closeRelatedDropDowns }: UseOptionsDropdownMenuParams): UseOptionsDropdownMenuReturn {
  const { t } = useTranslation();
  const { anchorEl, close, dismiss, getTriggerProps, isOpen, toggle } = usePopover({ hasPopup: 'menu' });

  const resolvedAriaLabel = ariaLabel ?? t('buttons:More options');

  // El menú vive dentro de cards clicables: frenamos la propagación al card
  // padre (paridad v1) antes de alternar el panel.
  const onTriggerClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      toggle();
    },
    [toggle],
  );

  const onDismiss = useCallback((): void => {
    dismiss();
    closeRelatedDropDowns?.();
  }, [closeRelatedDropDowns, dismiss]);

  const onClickInOption = useCallback(
    (item: OptionsDropdownMenuOption, event: SyntheticEvent): void => {
      if (!shouldKeepMenuOpen(item)) {
        close();
        closeRelatedDropDowns?.();
      }
      item.onClick?.(item, event);
    },
    [close, closeRelatedDropDowns],
  );

  return {
    anchorEl,
    isOpen,
    onClickInOption,
    onDismiss,
    onTriggerClick,
    resolvedAriaLabel,
    stopPropagationOnChildrenClick,
    triggerProps: getTriggerProps(),
  };
}

export default useOptionsDropdownMenu;
